import { Info } from "lucide-react";
import { Pending } from "@/components/Pending";
import { amountOf, longDate, normalizePlate, tl } from "@/lib/booking";
import { t } from "@/lib/i18n";

export function ParkNote({ testId = "park-fee-note", className = "" }) {
  return (
    <p className={`flex items-start gap-2 rounded-xl bg-[#FEF3C7] px-4 py-3 text-sm font-medium text-[#78350F] ${className}`} data-testid={testId}>
      <Info className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      {t("bilet.parkNote")}
    </p>
  );
}

function LegalText({ id, label, state }) {
  return (
    <details className="rounded-xl border border-line bg-white" data-testid={`booking-legal-${id}`}>
      <summary className="cursor-pointer min-h-[48px] flex items-center px-4 font-semibold text-forest">{label}</summary>
      <div className="px-4 pb-4 text-sm text-ink whitespace-pre-line max-h-72 overflow-y-auto">
        {state.value || <Pending testId={`booking-legal-${id}-pending`} />}
      </div>
    </details>
  );
}

export default function SummaryStep({ v, set, cfg, legal, error }) {
  const plate = v.plate.trim() ? normalizePlate(v.plate) : "";
  const amount = amountOf(cfg.prices, v, plate);
  const rows = [
    [t("bilet.summary.date"), longDate(v.date)],
    ...["adult", "child", "free"].filter((k) => v[k]).map((k) => [t(`bilet.counts.${k}`), String(v[k])]),
    [t("bilet.contact.name"), v.name.trim()],
    [t("bilet.contact.email"), v.email.trim()],
    ...(plate ? [[t("bilet.contact.plate"), plate]] : []),
    ...(plate && cfg.prices.vehicle ? [[t("bilet.summary.vehicle"), tl(cfg.prices.vehicle)]] : []),
  ];
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_380px]" data-testid="booking-summary-step">
      <div className="space-y-5">
        <dl className="rounded-2xl border border-line bg-white divide-y divide-line">
          {rows.map(([k, val]) => (
            <div key={k} className="flex flex-wrap justify-between gap-2 px-5 py-3">
              <dt className="text-ink-muted">{k}</dt>
              <dd className="font-semibold text-ink capitalize-first">{val}</dd>
            </div>
          ))}
        </dl>
        <LegalText id="kvkk" label={t("bilet.summary.kvkk")} state={legal.kvkk} />
        <LegalText id="terms" label={t("bilet.summary.terms")} state={legal.terms} />
        <label htmlFor="booking-accept" className="flex items-start gap-3 cursor-pointer min-h-[48px]">
          <input id="booking-accept" type="checkbox" checked={v.accept} onChange={(e) => set({ accept: e.target.checked })} aria-invalid={!!error} aria-describedby={error ? "booking-accept-error" : undefined} className="mt-1 h-5 w-5 shrink-0 accent-[#1E3A2B]" data-testid="booking-accept-checkbox" />
          <span className="text-sm text-ink">{t("bilet.summary.accept")}</span>
        </label>
        {error && <p id="booking-accept-error" role="alert" className="text-sm text-[#B42318]" data-testid="booking-accept-error">{error}</p>}
      </div>
      <aside className="rounded-3xl bg-forest text-cream p-6 sm:p-8 space-y-4 h-fit" data-testid="booking-amount-card">
        <p className="text-sm uppercase tracking-[0.18em] text-cream/80">{t("bilet.summary.due")}</p>
        <p className="font-serif text-5xl" data-testid="booking-amount-due">{tl(amount)}</p>
        <p className="text-sm font-semibold">{t("bilet.preNote")}</p>
        <ParkNote testId="booking-summary-park-note" />
      </aside>
    </div>
  );
}
